/**
 * The installed app's session (§10.2.1). Inside the Capacitor shell the page is not a browser
 * origin the API's cookie policy trusts, so sign-in asks for the `bearer` transport: the API hands
 * the token back in the body, it is kept here, and every API call presents it as
 * `Authorization: Bearer`. In a browser none of this runs — the HttpOnly cookie stays the only
 * credential and no token ever reaches script.
 *
 * HACK: the token is kept in the app's own sandboxed web storage. That storage is private to the
 * installed app, but not hardware-backed; the upgrade is a Keychain (iOS) / Keystore (Android)
 * secure-storage plugin behind these same three functions.
 */

const STORAGE_KEY = 'signbento.native_session'

/**
 * Tells the API a request comes from the installed app. Anyone can send it, so it is not a
 * credential: the dev API uses it only to let the app's sign-in skip Turnstile (DEV-005).
 */
export const NATIVE_CLIENT_HEADERS: Readonly<Record<string, string>> = {
  'x-signbento-client': 'native',
}

interface CapacitorGlobal {
  readonly isNativePlatform?: () => boolean
}

/** True only inside the Capacitor shell; a browser (including a phone browser) is false. */
export function isNativeApp(): boolean {
  // WKWebView exposes its message handlers to child frames too. Capacitor's browser runtime
  // therefore reports iOS even on the HTTPS challenge page embedded in the app. Only the
  // top-level shell owns native sessions/plugins; its hosted children are ordinary web pages.
  if (typeof window !== 'undefined' && window.parent !== window) return false
  const capacitor = (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor
  return capacitor?.isNativePlatform?.() === true
}

export function readNativeSession(): string | null {
  if (!isNativeApp()) return null
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null
  }
}

export function storeNativeSession(token: string): void {
  if (!isNativeApp()) return
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, token)
  } catch {
    // Without storage the app simply asks the person to sign in again next time.
  }
}

export function clearNativeSession(): void {
  try {
    globalThis.localStorage?.removeItem(STORAGE_KEY)
  } catch {
    // Nothing stored, nothing to clear.
  }
}

/** The credential header for a raw `fetch` that bypasses the transport (uploads, streams). */
export function sessionHeaders(): Record<string, string> {
  const token = readNativeSession()
  return token === null ? {} : { authorization: `Bearer ${token}` }
}
